import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import https from '../../utils/https';
import { QueryParams, buildQueryString } from '../types';

export interface Banner {
    _id?: string;
    title: string;
    description: string;
    link: string;
    type: string;
    priority: number;
    image: string;
    status: 'active' | 'inactive';
    createdAt?: string;
    updatedAt?: string;
}

interface BannerState {
    banners: Banner[];
    loading: boolean;
    error: string | null;
}

const initialState: BannerState = {
    banners: [],
    loading: false,
    error: null,
};

export const fetchBanners = createAsyncThunk('banner/fetchBanners', async (params: QueryParams | undefined, { rejectWithValue }) => {
    try {
        const mergedParams = { status: 'active', sort: { priority: 1 }, ...params };
        const queryString = buildQueryString(mergedParams);
        const response = await https.get(`banners${queryString}`);
        return response.data || [];
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to fetch banners');
    }
});

export const addBanner = createAsyncThunk('banner/addBanner', async (banner: FormData, { rejectWithValue }) => {
    try {
        const response = await https.post('banners', banner);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to add banner');
    }
});

export const updateBanner = createAsyncThunk('banner/updateBanner', async ({ id, banner }: { id: string; banner: FormData }, { rejectWithValue }) => {
    try {
        const response = await https.put(`banners/${id}`, banner);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to update banner');
    }
});

export const deleteBanner = createAsyncThunk('banner/deleteBanner', async (id: string, { rejectWithValue }) => {
    try {
        await https.delete(`banners/${id}`);
        return id;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to delete banner');
    }
});

const bannerSlice = createSlice({
    name: 'banner',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchBanners.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBanners.fulfilled, (state, action: PayloadAction<Banner[]>) => {
                state.loading = false;
                state.banners = action.payload;
            })
            .addCase(fetchBanners.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(addBanner.fulfilled, (state, action: PayloadAction<Banner>) => {
                state.banners.unshift(action.payload);
            })
            .addCase(updateBanner.fulfilled, (state, action: PayloadAction<Banner>) => {
                const index = state.banners.findIndex(b => b._id === action.payload._id);
                if (index !== -1) {
                    state.banners[index] = action.payload;
                }
            })
            .addCase(deleteBanner.fulfilled, (state, action: PayloadAction<string>) => {
                state.banners = state.banners.filter(b => b._id !== action.payload);
            });
    },
});

export default bannerSlice.reducer;
